import { PopperPlacementType } from '@mui/base';
import { useRef, useState } from 'react';

import { getHtmlCopy } from '../../i18n';
import { MenuButtonActions, MenuButton } from '../menu-button/MenuButton';
import { MenuItemButton } from '../menu-button/MenuItemButton';
import { Icon } from '../icon';
import { useLanguageSettings } from './use-language-settings';

export interface Language {
    name: string;
    localeCode: string;
}

export interface LanguageSelectorProps {
    classes?: string;
    id?: string;
    isAuthenticated?: boolean;
    languages: Language[];
    onLanguageChange?: (selectedLanguage?: Language) => void;
    placement?: PopperPlacementType;
    selectedLocale?: string;
}

export const LanguageSelector = ({
    classes,
    id,
    isAuthenticated,
    languages,
    onLanguageChange,
    placement,
    selectedLocale,
}: LanguageSelectorProps) => {
    const actions = useRef<MenuButtonActions>(null);
    const [selectedMenuItemLocale, setSelectedMenuItemLocale] = useState<
        string | undefined
    >(selectedLocale);
    const updateLanguageSettings = useLanguageSettings(!!isAuthenticated);

    const mapSelectedLocaleToLanguage = (
        localeCode: string | undefined
    ): Language | undefined =>
        languages?.find((language) => language.localeCode === localeCode);

    const menuItemClicked = (localeCode: string) => {
        setSelectedMenuItemLocale(localeCode);
        actions.current?.close();

        if (onLanguageChange) {
            const language = mapSelectedLocaleToLanguage(localeCode);
            onLanguageChange(language);
            return;
        }

        updateLanguageSettings(localeCode);
    };

    const selectedLanguage = mapSelectedLocaleToLanguage(
        selectedMenuItemLocale
    );

    return (
        <MenuButton
            actions={actions}
            classes={classes}
            icon={<Icon name="globe2" aria-label={getHtmlCopy('language')} />}
            id={id}
            label={selectedLanguage?.name ?? getHtmlCopy('language')}
            placement={placement}
        >
            {languages.map((language) => (
                <MenuItemButton
                    key={language.localeCode}
                    label={
                        <span lang={language.localeCode}>{language.name}</span>
                    }
                    onClick={() => menuItemClicked(language.localeCode)}
                    selected={selectedMenuItemLocale === language.localeCode}
                />
            ))}
        </MenuButton>
    );
};
